import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Star, ChevronLeft, ChevronRight, Quote, PenLine, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_REVIEWS = [
  {
    quote: "JAI HOMES delivered our childcare facility on schedule in South West Sydney. DA approvals and build execution were seamless.",
    role: "Investor Review",
    color: "#FF1744",
  },
  {
    quote: "Our knock-down rebuild in Box Hill turned out incredible. The team handled every engineering detail with total professionalism.",
    role: "Residential Client",
    color: "#D500F9",
  },
  {
    quote: "Outstanding execution on our medical fit-out. Highly recommend Surya and the team for commercial compliance.",
    role: "Commercial Client",
    color: "#00E676",
  },
];

const REVIEW_CATEGORIES = ["Residential", "Childcare", "Commercial Fit-Out", "Knock-Down Rebuild"];

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-[#FFC107] focus:ring-2 focus:ring-[#FFC107]/25";

const LeaveReviewModal = ({ open, onClose, onSubmitted }) => {
  const [form, setForm] = useState({ name: "", category: REVIEW_CATEGORIES[0], rating: 5, suburb: "", comments: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Please add your name.");
    if (!form.comments.trim()) return toast.error("Please write a few words about your project.");
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, form);
      toast.success("Thank you! Your review is with our team for moderation.");
      onSubmitted();
      onClose();
      setForm({ name: "", category: REVIEW_CATEGORIES[0], rating: 5, suburb: "", comments: "" });
    } catch {
      toast.error("Couldn't submit your review — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[90] overflow-y-auto bg-[#0A0E17]/75 backdrop-blur-sm"
      onClick={onClose}
      data-testid="review-modal-backdrop"
    >
      <div className="min-h-full flex items-start justify-center px-4 sm:px-6 pt-[120px] pb-10 pointer-events-none">
        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
          className="pointer-events-auto glass rounded-3xl border border-[#FFC107]/40 p-8 sm:p-9 max-w-lg w-full shadow-[0_0_60px_rgba(217,119,6,0.2),0_40px_120px_rgba(0,0,0,0.6)]"
          data-testid="review-modal"
        >
          <div className="flex items-start justify-between gap-4 mb-6">
            <div>
              <p className="font-display text-[10px] font-bold tracking-[0.3em] text-[#FFC107] mb-1.5">CLIENT FEEDBACK</p>
              <h3 className="font-display text-2xl font-extrabold tracking-tight text-white">Leave a Review</h3>
            </div>
            <button
              onClick={onClose}
              data-testid="review-modal-close"
              aria-label="Close review form"
              className="shrink-0 w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/50 hover:text-white hover:border-[#FFC107]/60 transition-colors duration-300"
            >
              <X size={16} />
            </button>
          </div>

          <div className="space-y-4">
            <input className={inputCls} placeholder="Full name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="review-name" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <select
                className={`${inputCls} appearance-none cursor-pointer [&>option]:bg-[#0A0E17]`}
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="review-category"
              >
                {REVIEW_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input className={inputCls} placeholder="Project suburb" value={form.suburb} onChange={(e) => setForm({ ...form, suburb: e.target.value })} data-testid="review-suburb" />
            </div>
            <div>
              <p className="font-display text-[11px] font-bold tracking-[0.2em] text-white/50 mb-2.5">YOUR RATING</p>
              <div className="flex gap-2" data-testid="review-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setForm({ ...form, rating: n })}
                    data-testid={`review-star-${n}`}
                    aria-label={`${n} star${n > 1 ? "s" : ""}`}
                    className="transition-transform duration-200 hover:scale-125"
                  >
                    <Star
                      size={26}
                      className={n <= form.rating ? "fill-[#FFC107] text-[#FFC107]" : "text-white/20"}
                      style={n <= form.rating ? { filter: "drop-shadow(0 0 8px rgba(255,193,7,0.6))" } : undefined}
                    />
                  </button>
                ))}
              </div>
            </div>
            <textarea
              className={`${inputCls} min-h-[110px] resize-none`}
              placeholder="Tell us about your build — communication, quality, timeline... *"
              value={form.comments}
              onChange={(e) => setForm({ ...form, comments: e.target.value })}
              data-testid="review-comments"
            />
          </div>

          <button
            onClick={submit}
            disabled={submitting}
            data-testid="review-submit-btn"
            className="mt-6 w-full rounded-full py-3.5 font-display text-sm font-bold text-[#0A0E17] bg-gradient-to-r from-[#F59E0B] to-[#FFC107] hover:shadow-[0_0_28px_rgba(255,193,7,0.5)] hover:scale-[1.01] transition-all duration-300 disabled:opacity-50"
          >
            {submitting ? "Submitting..." : "Submit My Review"}
          </button>
          <p className="mt-3 text-center text-[11px] text-white/40">
            Reviews are moderated by the JAI HOMES team before publishing.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export const ReviewsHub = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [approved, setApproved] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);

  const loadReviews = async () => {
    try {
      const { data } = await axios.get(`${API}/reviews`);
      setApproved(
        data.map((r) => ({
          quote: r.comments,
          role: `${r.category} · ${r.suburb || "NSW"} — ${r.name}`,
          color: "#FFC107",
        }))
      );
    } catch {
      /* reviews feed optional */
    }
  };

  useEffect(() => {
    loadReviews();
  }, []);

  const reviews = [...DEFAULT_REVIEWS, ...approved];

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % reviews.length), 6000);
    return () => clearInterval(t);
  }, [paused, reviews.length]);

  const review = reviews[index % reviews.length];

  return (
    <section
      className="relative px-6 sm:px-12 lg:px-20 py-24 bg-[#111827] before:absolute before:top-0 before:left-1/2 before:-translate-x-1/2 before:h-px before:w-[90%] before:max-w-7xl before:bg-gradient-to-r before:from-transparent before:via-amber-500/20 before:to-transparent"
      data-testid="reviews-section"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="max-w-7xl mx-auto glass rounded-3xl border border-white/10 p-8 sm:p-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-4"
          data-testid="rating-counter"
        >
          <p className="font-display text-[11px] font-bold tracking-[0.35em] text-[#FFC107] mb-4">
            CLIENT TRUST &amp; REVIEWS
          </p>
          <div className="flex items-end gap-3">
            <span className="font-display text-6xl font-extrabold tracking-tighter text-white leading-none">4.9</span>
            <span className="font-display text-xl text-white/40 mb-1">/ 5.0</span>
          </div>
          <div className="flex gap-1 mt-3" data-testid="rating-stars">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={20} className="fill-[#FFC107] text-[#FFC107]" style={{ filter: "drop-shadow(0 0 6px rgba(255,193,7,0.5))" }} />
            ))}
          </div>
          <p className="mt-3 text-xs text-white/50 leading-relaxed">
            Rated by NSW Homeowners &amp; Commercial Clients
          </p>
          <button
            onClick={() => setModalOpen(true)}
            data-testid="leave-review-btn"
            className="mt-6 inline-flex items-center gap-2 rounded-full px-6 py-3 font-display text-sm font-bold text-[#0A0E17] bg-gradient-to-r from-[#F59E0B] to-[#FFC107] hover:shadow-[0_0_28px_rgba(255,193,7,0.5)] hover:scale-[1.03] transition-all duration-300"
          >
            <PenLine size={15} /> Leave a Review
          </button>
        </motion.div>

        <div className="lg:col-span-8 relative" data-testid="review-carousel">
          <Quote size={36} className="absolute -top-2 -left-1 text-white/10" />
          <AnimatePresence mode="wait">
            <motion.blockquote
              key={index}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="pl-8"
              data-testid="review-card"
            >
              <p className="font-serif-lux italic text-xl sm:text-2xl text-white/90 leading-snug min-h-[96px]">
                "{review.quote}"
              </p>
              <footer className="mt-5 flex items-center gap-3 flex-wrap">
                <span
                  className="rounded-full px-3 py-1 font-display text-[11px] font-bold tracking-[0.15em] border"
                  style={{ color: review.color, borderColor: `${review.color}55`, background: `${review.color}12` }}
                >
                  {review.role.toUpperCase()}
                </span>
                <span className="text-[11px] text-white/40">Verified NSW Project</span>
              </footer>
            </motion.blockquote>
          </AnimatePresence>

          <div className="mt-7 pl-8 flex items-center gap-4">
            <button
              onClick={() => setIndex((index - 1 + reviews.length) % reviews.length)}
              data-testid="review-prev-btn"
              aria-label="Previous review"
              className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/60 hover:text-white hover:border-[#FFC107]/60 transition-colors duration-300"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={() => setIndex((index + 1) % reviews.length)}
              data-testid="review-next-btn"
              aria-label="Next review"
              className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/60 hover:text-white hover:border-[#FFC107]/60 transition-colors duration-300"
            >
              <ChevronRight size={16} />
            </button>
            <div className="flex gap-2 ml-2">
              {reviews.map((r, i) => (
                <button
                  key={i}
                  onClick={() => setIndex(i)}
                  data-testid={`review-dot-${i}`}
                  aria-label={`Go to review ${i + 1}`}
                  className="h-1.5 rounded-full transition-all duration-400"
                  style={{
                    width: i === index ? 28 : 10,
                    background: i === index ? "#FFC107" : "rgba(255,255,255,0.15)",
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <LeaveReviewModal open={modalOpen} onClose={() => setModalOpen(false)} onSubmitted={loadReviews} />
    </section>
  );
};
